import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Check, Bike, Car, Minus, Plus, Users, Crown, Ticket, Trophy, Shirt, Tag, X } from "lucide-react";
import { toast } from "sonner";
import { startPayment } from "@/lib/payment";
import { api, formatApiError } from "@/lib/api";
import { ActivityPicker, activitiesPayload, activitiesTotal } from "@/components/ActivityPicker";

const MAX_MEMBERS = 60;
const inr = (n) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;

const newMember = () => ({
  name: "", mobile: "", vehicle_type: "bike", vehicle_brand: "", vehicle_model: "",
  competition_entry: false, competition_description: "", tshirt_size: "",
});

export default function PassDialog({ open, onOpenChange, content, onPaid, passKey }) {
  const cfg = content?.[`${passKey}_pass`] || {};
  const pricing = cfg.pricing || { price_per_person: 0, pass_types: [] };
  const vehicles = content?.vehicles || { bike: [], car: [] };
  const comp = content?.bike_competition || {};
  const compFull = !!content?.competition_full;
  const isMeraki = passKey === "meraki";
  const label = isMeraki ? "Meraki Pass" : "Day Pass";
  const sizes = cfg.tshirt_sizes || [];
  const pricePerPerson = Number(pricing.price_per_person || 0);
  const compFee = Number(comp.entry_fee || 0);

  const passTypes = (pricing.pass_types || []).filter((p) => !(isMeraki && p.is_group));
  const ptVariable = (p) => !p.is_group && Number(p.max_members || p.people_per_pass || 1) > Number(p.people_per_pass || 1);

  const initPt = passTypes[0] || {};
  const initGroupSize = initPt && !initPt.is_group && Number(initPt.max_members) > Number(initPt.people_per_pass || 1) ? 1 : 0;
  const initPeople = initPt.is_group ? 0 : (ptVariable(initPt) ? initGroupSize : Math.max(Number(initPt.people_per_pass || 1), 0));
  const emptyForm = {
    name: "", mobile: "", email: "", instagram: "",
    pass_type_key: passTypes[0]?.key || "individual",
    pass_count: 1, group_size: initGroupSize,
    members: Array.from({ length: initPeople }, newMember),
    food_preference: "", drinks_preference: "",
  };

  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponErr, setCouponErr] = useState("");
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [activities, setActivities] = useState([]);
  const [selectedActs, setSelectedActs] = useState({});

  useEffect(() => {
    if (open) { setForm({ ...emptyForm }); setLoading(false); setCouponInput(""); setAppliedCoupon(null); setCouponErr(""); setSelectedActs({}); api.get("/activities").then((r) => setActivities(r.data)).catch(() => {}); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pt = useMemo(
    () => passTypes.find((p) => p.key === form.pass_type_key) || passTypes[0] || {},
    [form.pass_type_key, passTypes]
  );

  const isVariable = ptVariable(pt);
  const needsCount = pt.is_group || isVariable;
  const chosenCount = Number(form.group_size || 0);
  const peoplePerPass = pt.is_group
    ? chosenCount
    : (isVariable ? (chosenCount || Number(pt.people_per_pass || 1)) : Number(pt.people_per_pass || 1));
  const passCount = Math.max(Number(form.pass_count || 1), 1);
  const totalPeople = Math.max(peoplePerPass, 0) * passCount;
  const shownCount = Math.min(totalPeople, MAX_MEMBERS);

  // Resize members array to match total attendees
  useEffect(() => {
    setForm((f) => {
      const cur = f.members || [];
      if (cur.length === shownCount) return f;
      const next = [...cur];
      while (next.length < shownCount) next.push(newMember());
      next.length = shownCount;
      return { ...f, members: next };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownCount]);

  const setMember = (i, patch) =>
    setForm((f) => ({ ...f, members: f.members.map((m, idx) => (idx === i ? { ...m, ...patch } : m)) }));

  const tshirtPriceOf = (size) => Number(sizes.find((s) => s.size === size)?.price || 0);

  // Pricing (matches backend: discount on base only; add-ons full price)
  const discountPct = Number(pt.discount_percent || 0);
  const baseAmount = Math.max(peoplePerPass, 0) * pricePerPerson * passCount;
  const discountAmount = Math.round((baseAmount * discountPct) / 100);
  const tshirtTotal = form.members.reduce((s, m) => s + tshirtPriceOf(m.tshirt_size), 0);
  const compCount = comp.enabled ? form.members.filter((m) => m.competition_entry).length : 0;
  const compTotal = compCount * compFee;
  const total = baseAmount - discountAmount + tshirtTotal + compTotal;

  // Coupon discount (backend is source of truth; this mirrors it for live display)
  const couponMinOk = !appliedCoupon || total >= Number(appliedCoupon.min_order || 0);
  let couponDiscount = 0;
  if (appliedCoupon && couponMinOk) {
    couponDiscount = Math.round((total * Number(appliedCoupon.discount_value || 0)) / 100);
    const cap = Number(appliedCoupon.max_discount || 0);
    if (cap > 0) couponDiscount = Math.min(couponDiscount, cap);
    couponDiscount = Math.min(couponDiscount, total);
  }
  const finalTotal = total - couponDiscount;
  const actsAddon = activitiesTotal(activities, selectedActs);
  const grandTotal = finalTotal + actsAddon;

  const applyCoupon = async () => {
    const code = couponInput.trim();
    if (!code) { setCouponErr("Enter a coupon code"); return; }
    setApplyingCoupon(true); setCouponErr("");
    try {
      const { data } = await api.post("/coupons/validate", {
        code, pass_type: passKey, pass_type_key: form.pass_type_key, amount: total, email: form.email,
      });
      setAppliedCoupon(data.coupon);
      toast.success(data.message || "Coupon applied!");
    } catch (e) {
      setAppliedCoupon(null);
      setCouponErr(formatApiError(e?.response?.data?.detail) || "Invalid coupon code");
    } finally {
      setApplyingCoupon(false);
    }
  };

  const removeCoupon = () => { setAppliedCoupon(null); setCouponInput(""); setCouponErr(""); };

  const memberValid = (m, i) => {
    const nm = i === 0 ? form.name : m.name;
    const mob = i === 0 ? form.mobile : m.mobile;
    return nm && mob && m.vehicle_type && m.vehicle_brand && m.vehicle_model && m.tshirt_size &&
      (!m.competition_entry || m.competition_description);
  };

  const valid =
    form.name && form.mobile && form.email &&
    form.pass_type_key && passCount >= 1 &&
    (!needsCount || Number(form.group_size) >= 1) &&
    (!isMeraki || (form.food_preference && form.drinks_preference)) &&
    totalPeople >= 1 && form.members.length === shownCount &&
    form.members.every(memberValid);

  const submit = () => {
    if (!valid) { toast.error("Please complete all required fields for the lead contact and every attendee."); return; }
    const members = form.members.map((m, i) => ({
      ...m,
      name: i === 0 ? form.name : m.name,
      mobile: i === 0 ? form.mobile : m.mobile,
    }));
    const payload = {
      ...form, members,
      pass_type: passKey,
      pass_count: Number(form.pass_count),
      group_size: Number(form.group_size || 0),
      coupon_code: appliedCoupon && couponMinOk ? appliedCoupon.code : "",
      activities: activitiesPayload(activities, selectedActs),
    };
    onOpenChange(false);
    startPayment({ payload, setLoading, onSuccess: (_id, amount) => onPaid && onPaid(label, amount, form.name) });
  };

  const stepCount = (delta) => {
    const max = Number(pt.max_passes || 10);
    set("pass_count", Math.min(Math.max(1, Number(form.pass_count || 1) + delta), max));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`${passKey}-pass-dialog`} className="bg-[#121212] border-border max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-800 uppercase tracking-tight flex items-center gap-2">
            <Ticket className="h-6 w-6 text-[#FF4500]" /> {label}
          </DialogTitle>
        </DialogHeader>

        {cfg.what_you_get?.length > 0 && (
          <div data-testid={`${passKey}-what-you-get`} className="rounded-xl border border-[#FF4500]/30 bg-[#FF4500]/[0.06] p-4">
            <h4 className="font-display text-lg font-700 uppercase tracking-tight mb-2 flex items-center gap-1.5">
              {isMeraki ? <Crown className="h-4 w-4 text-[#FF4500]" /> : null} What You Get
            </h4>
            <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5">
              {cfg.what_you_get.map((it, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[#E4E4E7]"><Check className="h-4 w-4 text-[#FF4500] mt-0.5 shrink-0" /><span>{it}</span></li>
              ))}
            </ul>
          </div>
        )}

        <div className="space-y-5 mt-1">
          {/* Step 1: Pass type */}
          <Section title="Step 1 · Choose your pass type">
            <RadioGroup value={form.pass_type_key} onValueChange={(v) => { set("pass_type_key", v); set("pass_count", 1); const np = passTypes.find((p) => p.key === v); set("group_size", np && !np.is_group && Number(np.max_members) > Number(np.people_per_pass || 1) ? 1 : 0); }} className="grid sm:grid-cols-2 gap-2.5">
              {passTypes.map((p) => {
                const ppl = p.is_group ? "you choose" : (ptVariable(p) ? `up to ${p.max_members} people` : `${p.people_per_pass} ${p.people_per_pass > 1 ? "people" : "person"}`);
                const showPrice = !p.is_group && !ptVariable(p);
                return (
                  <label key={p.key} className={`rounded-lg border p-3 cursor-pointer transition-colors ${form.pass_type_key === p.key ? "border-[#FF4500] bg-[#FF4500]/10" : "border-border hover:border-[#3f3f46]"}`}>
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-2"><RadioGroupItem value={p.key} data-testid={`${passKey}-ptype-${p.key}`} /><span className="text-sm font-semibold">{p.label}</span></span>
                      {p.discount_percent > 0 && <span className="text-[10px] font-bold text-[#25D366] bg-[#25D366]/15 rounded-full px-2 py-0.5">{p.discount_percent}% OFF</span>}
                    </div>
                    <div className="mt-1 pl-6 text-xs text-[#A1A1AA]">{ppl}{showPrice && ` · ${inr(p.people_per_pass * pricePerPerson * (1 - p.discount_percent / 100))}/pass`}</div>
                  </label>
                );
              })}
            </RadioGroup>
            {needsCount && (
              <div className="mt-3 max-w-xs">
                <Field label={pt.is_group ? `Group size (${pt.min_members}–${pt.max_members})` : `Number of attendees (1–${pt.max_members})`} required>
                  <Input data-testid={`${passKey}-group-size`} type="number" min={pt.is_group ? pt.min_members : 1} max={pt.max_members} value={form.group_size || ""} onChange={(e) => set("group_size", e.target.value)} placeholder={pt.is_group ? "e.g. 8" : "e.g. 3"} />
                </Field>
              </div>
            )}
            <div className="mt-3 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="text-xs uppercase tracking-wide text-[#A1A1AA]">Number of passes</span>
                <Button type="button" data-testid={`${passKey}-qty-minus`} onClick={() => stepCount(-1)} variant="outline" size="icon" className="rounded-full border-border h-9 w-9"><Minus className="h-4 w-4" /></Button>
                <span data-testid={`${passKey}-qty-value`} className="font-display text-2xl font-800 w-8 text-center">{form.pass_count}</span>
                <Button type="button" data-testid={`${passKey}-qty-plus`} onClick={() => stepCount(1)} variant="outline" size="icon" className="rounded-full border-border h-9 w-9"><Plus className="h-4 w-4" /></Button>
              </div>
              <div className="text-xs text-[#71717A] flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {totalPeople} {totalPeople === 1 ? "attendee" : "attendees"} total</div>
            </div>
          </Section>

          {/* Step 2: Lead contact */}
          <Section title="Step 2 · Your details (lead contact · Attendee 1)">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Name" required><Input data-testid={`${passKey}-name-input`} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Full name" /></Field>
              <Field label="Mobile" required><Input data-testid={`${passKey}-mobile-input`} value={form.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="10-digit number" /></Field>
              <Field label="Email" required><Input data-testid={`${passKey}-email-input`} type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" /></Field>
              <Field label="Instagram"><Input data-testid={`${passKey}-instagram-input`} value={form.instagram} onChange={(e) => set("instagram", e.target.value)} placeholder="@handle" /></Field>
            </div>
          </Section>

          {isMeraki && (
            <Section title="Preferences">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Food Preference" required>
                  <RadioGroup value={form.food_preference} onValueChange={(v) => set("food_preference", v)} className="flex gap-4 mt-1">
                    {(cfg.food_options || []).map((o) => <label key={o} className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value={o} data-testid={`meraki-food-${o}`} /><span className="text-sm">{o}</span></label>)}
                  </RadioGroup>
                </Field>
                <Field label="Drinks Preference" required>
                  <RadioGroup value={form.drinks_preference} onValueChange={(v) => set("drinks_preference", v)} className="flex gap-4 mt-1">
                    {(cfg.drinks_options || []).map((o) => <label key={o} className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value={o} data-testid={`meraki-drinks-${o}`} /><span className="text-sm">{o}</span></label>)}
                  </RadioGroup>
                </Field>
              </div>
            </Section>
          )}

          {/* Step 3: Attendee details */}
          {shownCount > 0 && (
            <Section title={`Step 3 · Attendee details (${shownCount})`}>
              {totalPeople > MAX_MEMBERS && <p className="text-xs text-[#FF7043] mb-2">Showing first {MAX_MEMBERS} attendees.</p>}
              <div className="space-y-3">
                {form.members.map((m, i) => {
                  const brands = vehicles[m.vehicle_type] || [];
                  const models = brands.find((b) => b.brand === m.vehicle_brand)?.models || [];
                  const tPrice = tshirtPriceOf(m.tshirt_size);
                  const perAttendee = pricePerPerson + tPrice + (m.competition_entry ? compFee : 0);
                  return (
                    <div key={i} className="rounded-xl border border-border p-4" data-testid={`${passKey}-member-${i}`}>
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs uppercase tracking-widest text-[#FF7043] font-700">Attendee {i + 1}{i === 0 ? " · Lead" : ""}</span>
                        <span data-testid={`${passKey}-member-${i}-amount`} className="text-xs text-[#A1A1AA]">Amount: <span className="text-white font-semibold">{inr(perAttendee)}</span></span>
                      </div>

                      {/* a) Basic details */}
                      {i === 0 ? (
                        <div className="rounded-lg bg-white/[0.03] border border-border/60 px-3 py-2 text-sm text-[#A1A1AA] mb-3">
                          Same as lead contact: <span className="text-white font-medium">{form.name || "—"}</span> · {form.mobile || "—"}
                        </div>
                      ) : (
                        <div className="grid sm:grid-cols-2 gap-2.5 mb-3">
                          <Input data-testid={`${passKey}-member-${i}-name`} value={m.name} onChange={(e) => setMember(i, { name: e.target.value })} placeholder="Name *" />
                          <Input data-testid={`${passKey}-member-${i}-mobile`} value={m.mobile} onChange={(e) => setMember(i, { mobile: e.target.value })} placeholder="Mobile *" />
                        </div>
                      )}

                      {/* b) Vehicle */}
                      <Label className="text-[11px] uppercase tracking-wide text-[#A1A1AA]">How will you be joining us? <span className="text-[#FF4500]">*</span></Label>
                      <RadioGroup value={m.vehicle_type} onValueChange={(v) => setMember(i, { vehicle_type: v, vehicle_brand: "", vehicle_model: "" })} className="flex gap-3 mt-1.5 mb-2.5">
                        {[["bike", "Bike", Bike], ["car", "Car", Car]].map(([val, lbl, Icon]) => (
                          <label key={val} className={`flex-1 flex items-center gap-2 rounded-lg border p-2.5 cursor-pointer transition-colors ${m.vehicle_type === val ? "border-[#FF4500] bg-[#FF4500]/10" : "border-border hover:border-[#3f3f46]"}`}>
                            <RadioGroupItem value={val} data-testid={`${passKey}-member-${i}-vehicle-${val}`} />
                            <Icon className="h-4 w-4 text-[#FF4500]" /><span className="text-sm font-medium">{lbl}</span>
                          </label>
                        ))}
                      </RadioGroup>
                      <div className="grid grid-cols-2 gap-2.5 mb-3">
                        <Select value={m.vehicle_brand} onValueChange={(v) => setMember(i, { vehicle_brand: v, vehicle_model: "" })}>
                          <SelectTrigger data-testid={`${passKey}-member-${i}-brand`}><SelectValue placeholder="Brand *" /></SelectTrigger>
                          <SelectContent className="bg-[#1A1A1A] border-border">
                            {brands.map((b) => <SelectItem key={b.brand} value={b.brand}>{b.brand}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Select value={m.vehicle_model} onValueChange={(v) => setMember(i, { vehicle_model: v })} disabled={!m.vehicle_brand}>
                          <SelectTrigger data-testid={`${passKey}-member-${i}-model`}><SelectValue placeholder={m.vehicle_brand ? "Model *" : "Pick brand first"} /></SelectTrigger>
                          <SelectContent className="bg-[#1A1A1A] border-border">
                            {models.map((mo) => <SelectItem key={mo} value={mo}>{mo}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* c) Bike competition */}
                      {comp.enabled && (
                        <div className="mb-3">
                          <Label className="text-[11px] uppercase tracking-wide text-[#A1A1AA] flex items-center gap-1.5">
                            <Trophy className="h-3.5 w-3.5 text-[#FF4500]" /> Show Your Bike Competition
                            {compFull && !m.competition_entry && <span data-testid={`${passKey}-member-${i}-comp-full`} className="ml-2 rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-semibold text-[#FF7043]">Entries Full</span>}
                          </Label>
                          <RadioGroup value={m.competition_entry ? "Yes" : "No"} onValueChange={(v) => setMember(i, { competition_entry: v === "Yes" })} className="flex gap-6 mt-1.5">
                            <label className={`flex items-center gap-2 ${compFull && !m.competition_entry ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
                              <RadioGroupItem value="Yes" disabled={compFull && !m.competition_entry} data-testid={`${passKey}-member-${i}-comp-yes`} />
                              <span className="text-sm">Yes {compFee > 0 && <span className="text-[#FF7043]">(+{inr(compFee)})</span>}</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <RadioGroupItem value="No" data-testid={`${passKey}-member-${i}-comp-no`} /><span className="text-sm">No</span>
                            </label>
                          </RadioGroup>
                          {m.competition_entry && (
                            <Textarea data-testid={`${passKey}-member-${i}-comp-description`} rows={2} className="mt-2" value={m.competition_description} onChange={(e) => setMember(i, { competition_description: e.target.value })} placeholder={comp.description_label || "Tell us about your bike"} />
                          )}
                        </div>
                      )}

                      {/* d) T-shirt size */}
                      <Label className="text-[11px] uppercase tracking-wide text-[#A1A1AA] flex items-center gap-1.5"><Shirt className="h-3.5 w-3.5 text-[#FF4500]" /> T-Shirt Size <span className="text-[#FF4500]">*</span></Label>
                      <Select value={m.tshirt_size} onValueChange={(v) => setMember(i, { tshirt_size: v })}>
                        <SelectTrigger data-testid={`${passKey}-member-${i}-tshirt`} className="mt-1.5"><SelectValue placeholder="Select size" /></SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-border">
                          {sizes.map((s) => <SelectItem key={s.size} value={s.size}>{s.size} — {Number(s.price) > 0 ? `+${inr(s.price)}` : "Free"}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })}
              </div>
            </Section>
          )}

          {/* Add-on: Activity tickets */}
          {activities.length > 0 && (
            <Section title="Add Activity Tickets (optional)">
              <ActivityPicker activities={activities} selected={selectedActs} setSelected={setSelectedActs} testPrefix={`${passKey}-activity`} />
            </Section>
          )}

          {/* Step 4: Pricing summary */}
          <Section title="Step 4 · Pricing summary">
            <div data-testid={`${passKey}-pricing-summary`} className="rounded-xl border border-border bg-white/[0.02] p-4 space-y-1.5 text-sm">
              <Row label="Pass Type" value={pt.label} />
              <Row label="Number of Passes" value={form.pass_count} />
              <Row label="People per Pass" value={Math.max(peoplePerPass, 0)} />
              <Row label="Total Attendees" value={totalPeople} />
              <div className="my-2 border-t border-border/60" />
              <Row label="Base Amount" value={inr(baseAmount)} />
              {tshirtTotal > 0 && <Row label="T-Shirt Charges" value={inr(tshirtTotal)} />}
              {compTotal > 0 && <Row label={`Bike Competition Fee${compCount > 1 ? ` (×${compCount})` : ""}`} value={inr(compTotal)} />}
              {discountAmount > 0 && <Row label={`Discount (${discountPct}%)`} value={`−${inr(discountAmount)}`} accent="#25D366" />}
              {couponDiscount > 0 && <Row label={`Coupon (${appliedCoupon.code})`} value={`−${inr(couponDiscount)}`} accent="#25D366" />}
              {actsAddon > 0 && <Row label="Activity Tickets" value={inr(actsAddon)} />}
              <div className="my-2 border-t border-border/60" />
              <div className="flex items-center justify-between">
                <span className="font-display uppercase tracking-wide text-[#FF7043] font-700">Final Amount</span>
                <span data-testid={`${passKey}-total`} className="font-display text-2xl font-900 text-white">{inr(grandTotal)}</span>
              </div>
            </div>

            {/* Apply coupon */}
            <div className="mt-3">
              {appliedCoupon && couponMinOk ? (
                <div data-testid={`${passKey}-coupon-applied`} className="flex items-center justify-between rounded-xl border border-[#25D366]/40 bg-[#25D366]/[0.08] px-4 py-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Tag className="h-4 w-4 text-[#25D366]" />
                    <span className="font-semibold text-white">{appliedCoupon.code}</span>
                    <span className="text-[#25D366]">You saved {inr(couponDiscount)}</span>
                  </div>
                  <button data-testid={`${passKey}-coupon-remove`} onClick={removeCoupon} className="text-[#A1A1AA] hover:text-white transition-colors flex items-center gap-1 text-xs">
                    <X className="h-3.5 w-3.5" /> Remove
                  </button>
                </div>
              ) : (
                <div>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#71717A]" />
                      <Input
                        data-testid={`${passKey}-coupon-input`}
                        value={couponInput}
                        onChange={(e) => { setCouponInput(e.target.value.toUpperCase()); setCouponErr(""); }}
                        onKeyDown={(e) => e.key === "Enter" && applyCoupon()}
                        placeholder="Have a coupon code?"
                        className="pl-9 uppercase"
                      />
                    </div>
                    <Button data-testid={`${passKey}-coupon-apply`} type="button" onClick={applyCoupon} disabled={applyingCoupon} variant="outline" className="border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-white rounded-lg px-5">
                      {applyingCoupon ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
                    </Button>
                  </div>
                  {couponErr && <p data-testid={`${passKey}-coupon-error`} className="mt-1.5 text-xs text-[#FF6B6B]">{couponErr}</p>}
                  {appliedCoupon && !couponMinOk && <p data-testid={`${passKey}-coupon-error`} className="mt-1.5 text-xs text-[#FF6B6B]">Minimum order of {inr(appliedCoupon.min_order)} needed for {appliedCoupon.code}.</p>}
                </div>
              )}
            </div>
          </Section>

          <Button data-testid={`${passKey}-pay-button`} onClick={submit} disabled={loading} className="w-full h-14 rounded-full bg-[#FF4500] hover:bg-[#FF5722] text-white text-lg font-bold uppercase tracking-wide transition-colors disabled:opacity-60">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : `Pay ${inr(grandTotal)}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="font-display text-sm font-700 uppercase tracking-widest text-[#FF7043] mb-2.5">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wide text-[#A1A1AA]">{label} {required && <span className="text-[#FF4500]">*</span>}</Label>
      {children}
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[#A1A1AA]">{label}</span>
      <span className="font-medium" style={accent ? { color: accent } : undefined}>{value}</span>
    </div>
  );
}
